import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * IMPORTANT NOTICE: Feature flags added here must also be added to the `packages/feature-flags/client-feature-flags.ts` file
 * or they will not be available to check in this file!
 */

class CopilotFeatureFlags {
  get immersiveFigmaIntegration() {
    return isFeatureEnabled('copilot_immersive_figma_integration')
  }

  get planningAgent() {
    return isFeatureEnabled('copilot_immersive_planning_agent')
  }

  get draftIssueCodeSearch() {
    return isFeatureEnabled('copilot_api_draft_issue_code_search')
  }

  get focusTextAreaInsteadOfWorkbench() {
    return isFeatureEnabled('copilot_focus_text_area_instead_of_workbench')
  }

  get forceLegacyChatDefaultModel() {
    return isFeatureEnabled('copilot_api_force_legacy_base_chat_model')
  }

  get draftUpdateIssue() {
    return isFeatureEnabled('copilot_api_github_draft_update_issue_skill')
  }

  get domPageContext() {
    return isFeatureEnabled('copilot_use_dom_page_context')
  }

  /**
   * When enabled, the interview survey dialog replaces the 'Give feedback' dialog in copilot chat.
   * @returns {boolean} Whether the feature is enabled.
   */
  get copilotChatInterviewSurvey() {
    return isFeatureEnabled('copilot_chat_interview_survey')
  }

  get attachMultipleImages() {
    return isFeatureEnabled('copilot_chat_attach_multiple_images')
  }

  /** Feature flag for text selection attachments in file previews */
  get selectionAttachments() {
    return isFeatureEnabled('copilot_chat_selection_attachments')
  }

  get customCopilots() {
    return isFeatureEnabled('copilot_custom_copilots')
  }

  get immersiveStructuredModelPicker() {
    return isFeatureEnabled('copilot_immersive_structured_model_picker')
  }

  get spacesDuplicate() {
    return isFeatureEnabled('copilot_spaces_duplicate')
  }

  get customCopilotsFeaturePreview() {
    return isFeatureEnabled('copilot_custom_copilots_feature_preview')
  }

  get clientDOMSkills() {
    return isFeatureEnabled('copilot_client_dom_skills')
  }

  get dotcomChatClientSideSkills() {
    return isFeatureEnabled('dotcom_chat_client_side_skills')
  }

  get loopsPlugin() {
    return isFeatureEnabled('copilot_pipes')
  }

  get workbenchPlugin() {
    return isFeatureEnabled('copilot_workbench')
  }

  get copilotChatOpeningThreadSwitch() {
    return isFeatureEnabled('copilot_chat_opening_thread_switch')
  }

  get copilotReadSharedConversation() {
    return isFeatureEnabled('copilot_read_shared_conversation')
  }

  get copilotDuplicateThread() {
    return isFeatureEnabled('copilot_duplicate_thread')
  }

  get copilotShareActiveSubthread() {
    return isFeatureEnabled('copilot_share_active_subthread')
  }

  get copilotChatO1Tools() {
    return isFeatureEnabled('copilot_api_tools_for_non_streaming_models')
  }

  get bingGroundingServiceEnabled() {
    return isFeatureEnabled('copilot_bing_search_use_azure_ai_agent_service')
  }

  get bingGroundingUIEnabled() {
    // The new UI can only be enabled if the backend service is enabled
    return isFeatureEnabled('copilot_bing_search_use_grounding_ui') && this.bingGroundingServiceEnabled
  }

  get showDeepCodeSearchButton() {
    return (
      isFeatureEnabled('copilot_show_deep_code_search_button') && isFeatureEnabled('copilot_api_search_agent_skill')
    )
  }

  get showSubIssuesManageWithCopilotButton() {
    return isFeatureEnabled('copilot_show_copilot_sub_issues_button_on_issues_page')
  }

  get deleteAllConversations() {
    return isFeatureEnabled('copilot_delete_all_conversations')
  }

  get staffPromptDialog() {
    return isFeatureEnabled('copilot_staff_prompt_dialog')
  }

  get workbenchTerminal() {
    return isFeatureEnabled('copilot_workbench_terminal')
  }

  get workbenchPreviewAnalytics() {
    return isFeatureEnabled('copilot_workbench_preview_analytics')
  }

  get workbenchRefreshOnWsod() {
    return isFeatureEnabled('copilot_workbench_refresh_on_wsod')
  }

  get workbenchShowConnectionReloadBanner() {
    return isFeatureEnabled('copilot_workbench_connection_reload_banner')
  }

  get visionAllowedInClaude() {
    return isFeatureEnabled('copilot_chat_vision_in_claude')
  }

  get visionSkipThreadCreate() {
    return isFeatureEnabled('copilot_chat_vision_skip_thread_create')
  }

  get premiumRequestQuotasEnabled() {
    return isFeatureEnabled('copilot_premium_request_quotas')
  }

  get freeToPaidTelemetry() {
    return isFeatureEnabled('copilot_free_to_paid_telem')
  }

  get freeToPaidSettingsUpgrade() {
    return isFeatureEnabled('copilot_ftp_settings_upgrade')
  }

  get singleUserIteration() {
    return isFeatureEnabled('copilot_spark_single_user_iteration')
  }

  get freeToPaidYourCopilotSettings() {
    return isFeatureEnabled('copilot_ftp_your_copilot_settings')
  }

  get freeToPaidUpgradeToProFromModels() {
    return isFeatureEnabled('copilot_ftp_upgrade_to_pro_from_models')
  }

  get commitOnDefaultBranch() {
    return isFeatureEnabled('spark_commit_on_default_branch')
  }

  get spacesImagesEnabled() {
    return isFeatureEnabled('copilot_custom_copilots_images')
  }

  get knowledgeBasesDisabled() {
    return isFeatureEnabled('kb_sunset') && !isFeatureEnabled('kb_sunset_override')
  }

  get multiFilePickerAttachment() {
    return isFeatureEnabled('copilot_chat_multi_file_picker_attachment')
  }

  get copilotPersistEditedDraftIssues() {
    return isFeatureEnabled('copilot_immersive_draft_issue_persist_edited_issues_in_session')
  }

  get sparkUseBillingHeaders() {
    return isFeatureEnabled('copilot_spark_use_billing_headers')
  }

  get workbenchDefaultSonnet4() {
    return isFeatureEnabled('workbench_default_sonnet4')
  }

  get sparkAllowEmptyCommit() {
    return isFeatureEnabled('copilot_spark_allow_empty_commit')
  }

  get copilotThirdPartyMcp() {
    return isFeatureEnabled('copilot_api_dotcom_chat_use_third_party_mcp_servers')
  }

  get hyperspaceDiffView() {
    return isFeatureEnabled('hyperspace_diff_view')
  }

  get disableModelPickerWhileStreaming() {
    return isFeatureEnabled('copilot_chat_disable_model_picker_while_streaming')
  }

  get sparkSyncRepositoryAfterIteration() {
    return isFeatureEnabled('spark_sync_repository_after_iteration')
  }

  get sparkUseSeparateSuggestionArgs() {
    return isFeatureEnabled('copilot_spark_use_separate_suggestion_args')
  }

  get groupNotifications() {
    return isFeatureEnabled('copilot_chat_group_notifications')
  }

  get freeToPaidHyperspaceUpgradePrompt() {
    return isFeatureEnabled('copilot_ftp_hyperspace_upgrade_prompt')
  }

  get reduceChatQuotaChecks() {
    return isFeatureEnabled('copilot_chat_reduce_quota_checks')
  }

  get kbSemanticApiMigration() {
    return isFeatureEnabled('kb_semantic_api_migration')
  }

  get spacesInputMenuSelect() {
    return isFeatureEnabled('copilot_spaces_input_menu_select')
  }

  get requestTracing() {
    return isFeatureEnabled('copilot_api_staff_only_tracing')
  }

  get copilotSpacesReadAccessToUserOwnedSpaces() {
    return isFeatureEnabled('copilot_spaces_read_access_to_user_owned_spaces')
  }

  get copilotSpacesPublicAccessToUserOwnedSpaces() {
    return isFeatureEnabled('copilot_spaces_public_access_to_user_owned_spaces')
  }

  get copilotGlobalOverlayV2() {
    return isFeatureEnabled('copilot_global_overlay_v2')
  }

  get copilotWorkbenchSyntheticGeneration() {
    return isFeatureEnabled('copilot_workbench_synthetic_generation')
  }

  get copilotWorkbenchAgentSeedTool() {
    return isFeatureEnabled('copilot_workbench_agent_seed_tool')
  }

  get capiErrorResponseTelemetry() {
    return isFeatureEnabled('copilot_capi_error_response_telemetry')
  }

  get sparkWriteIterationHistoryToGit() {
    return isFeatureEnabled('copilot_spark_write_iteration_history_to_git')
  }

  get sparkReadIterationHistoryFromGit() {
    return isFeatureEnabled('copilot_spark_read_iteration_history_from_git')
  }

  get sparkReadIterationHistoryFromGitV2() {
    return isFeatureEnabled('copilot_spark_read_iteration_history_from_git_v2')
  }

  get copilotWorkbenchCache() {
    return isFeatureEnabled('copilot_workbench_cache')
  }

  get copilotWorkbenchGrepGlob() {
    return isFeatureEnabled('copilot_workbench_grepglob')
  }

  get copilotWorkbenchRatelimitFallback() {
    return isFeatureEnabled('copilot_workbench_ratelimit_fallback')
  }

  get sparkUseNewDataLayer() {
    return isFeatureEnabled('copilot_spark_new_data_layer')
  }

  get sparkUseProgressiveErrorHandling() {
    return isFeatureEnabled('copilot_spark_progressive_error_handling')
  }

  get sparkLoadingWebGL() {
    return isFeatureEnabled('copilot_spark_loading_webgl')
  }

  get fileBlockRefMatching() {
    return isFeatureEnabled('copilot_file_block_ref_matching')
  }

  get stableConversationView() {
    return isFeatureEnabled('copilot_stable_conversation_view')
  }

  get copilotRedirectDiffViewToImmersive() {
    return isFeatureEnabled('copilot_chat_diff_view_redirect')
  }

  get codingAgentFileReference() {
    return isFeatureEnabled('copilot_agent_tasks_btn_file_reference')
  }

  get loopsShareButton() {
    return isFeatureEnabled('copilot_loops_share_button')
  }

  get mcpToolDescriptions() {
    return isFeatureEnabled('copilot_mcp_tool_descriptions')
  }

  get copilotWorkbenchUseSinglePrompt() {
    return isFeatureEnabled('copilot_workbench_use_single_prompt')
  }
}

export const copilotFeatureFlags = new CopilotFeatureFlags()
export type {CopilotFeatureFlags}
